import React, { useState, Component } from 'react';
import { LayoutIcon, SmartphoneIcon, PaletteIcon, ZapIcon, CodeIcon, LayersIcon, ActivityIcon, MusicIcon, RocketIcon, ClipboardIcon, BookOpenIcon, SlidersIcon, CalendarIcon, MailIcon, TypeIcon, PackageIcon, CopyIcon, FileTextIcon, BriefcaseIcon, ImageIcon, ChevronUpIcon, ChevronDownIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
interface CategoryTileProps {
  title: string;
  icon: React.ReactNode;
  color: string;
  delay: number;
  onClick: () => void;
}
const CategoryTile: React.FC<CategoryTileProps> = ({
  title,
  icon,
  color,
  delay,
  onClick
}) => {
  return <motion.div className="group" initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3,
    delay: delay * 0.05
  }} data-id="element-260">
      <button onClick={e => {
      // Stop event propagation to prevent other handlers from interfering
      e.stopPropagation();
      // Call the click handler
      onClick();
    }} className={`flex flex-col items-center justify-center p-6 rounded-xl bg-white dark:bg-[#162825] border border-gray-100 dark:border-[#1A3330] hover:shadow-lg transition-all duration-300 h-full w-full`} data-id="element-261">
        <div className={`w-16 h-16 rounded-full ${color} flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110`} data-id="element-262">
          {icon}
        </div>
        <h3 className="text-lg font-semibold text-gray-800 dark:text-[#F8FAF9] text-center" data-id="element-263">
          {title}
        </h3>
      </button>
    </motion.div>;
};
// Use a functional component wrapper to access the navigate hook
const CategoryGridContent = () => {
  const navigate = useNavigate();
  const [showAllCategories, setShowAllCategories] = useState(false);
  const categories = [{
    title: 'Website Templates',
    icon: <LayoutIcon className="h-8 w-8 text-white" data-id="element-264" />,
    color: 'bg-gradient-to-br from-blue-500 to-blue-600 dark:from-blue-400 dark:to-blue-500'
  }, {
    title: 'Mobile App Kits',
    icon: <SmartphoneIcon className="h-8 w-8 text-white" data-id="element-265" />,
    color: 'bg-gradient-to-br from-purple-500 to-purple-600 dark:from-purple-400 dark:to-purple-500'
  }, {
    title: 'UI Kits',
    icon: <PaletteIcon className="h-8 w-8 text-white" data-id="element-266" />,
    color: 'bg-gradient-to-br from-pink-500 to-pink-600 dark:from-pink-400 dark:to-pink-500'
  }, {
    title: 'AI Prompt Packs',
    icon: <ZapIcon className="h-8 w-8 text-white" data-id="element-267" />,
    color: 'bg-gradient-to-br from-yellow-500 to-yellow-600 dark:from-yellow-400 dark:to-yellow-500'
  }, {
    title: 'Component Libraries',
    icon: <CodeIcon className="h-8 w-8 text-white" data-id="element-268" />,
    color: 'bg-gradient-to-br from-green-500 to-green-600 dark:from-green-400 dark:to-green-500'
  }, {
    title: 'Design Systems',
    icon: <LayersIcon className="h-8 w-8 text-white" data-id="element-269" />,
    color: 'bg-gradient-to-br from-indigo-500 to-indigo-600 dark:from-indigo-400 dark:to-indigo-500'
  }, {
    title: 'Animations',
    icon: <ActivityIcon className="h-8 w-8 text-white" data-id="element-270" />,
    color: 'bg-gradient-to-br from-red-500 to-red-600 dark:from-red-400 dark:to-red-500'
  }, {
    title: 'Audio Packs',
    icon: <MusicIcon className="h-8 w-8 text-white" data-id="element-271" />,
    color: 'bg-gradient-to-br from-orange-500 to-orange-600 dark:from-orange-400 dark:to-orange-500'
  }, {
    title: 'No-Code MVPs',
    icon: <RocketIcon className="h-8 w-8 text-white" data-id="element-272" />,
    color: 'bg-gradient-to-br from-teal-500 to-teal-600 dark:from-teal-400 dark:to-teal-500'
  }, {
    title: 'Notion Templates',
    icon: <ClipboardIcon className="h-8 w-8 text-white" data-id="element-273" />,
    color: 'bg-gradient-to-br from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0]'
  }, {
    title: 'eBooks & PDF Guides',
    icon: <BookOpenIcon className="h-8 w-8 text-white" data-id="element-274" />,
    color: 'bg-gradient-to-br from-blue-500 to-blue-600 dark:from-blue-400 dark:to-blue-500'
  }, {
    title: 'Course Slides & Templates',
    icon: <SlidersIcon className="h-8 w-8 text-white" data-id="element-275" />,
    color: 'bg-gradient-to-br from-purple-500 to-purple-600 dark:from-purple-400 dark:to-purple-500'
  }, {
    title: 'Printable Planners',
    icon: <CalendarIcon className="h-8 w-8 text-white" data-id="element-276" />,
    color: 'bg-gradient-to-br from-pink-500 to-pink-600 dark:from-pink-400 dark:to-pink-500'
  }, {
    title: 'Email Templates & Flows',
    icon: <MailIcon className="h-8 w-8 text-white" data-id="element-277" />,
    color: 'bg-gradient-to-br from-yellow-500 to-yellow-600 dark:from-yellow-400 dark:to-yellow-500'
  }, {
    title: 'Copywriting Packs',
    icon: <CopyIcon className="h-8 w-8 text-white" data-id="element-278" />,
    color: 'bg-gradient-to-br from-green-500 to-green-600 dark:from-green-400 dark:to-green-500'
  }, {
    title: 'Fonts & Typefaces',
    icon: <TypeIcon className="h-8 w-8 text-white" data-id="element-279" />,
    color: 'bg-gradient-to-br from-indigo-500 to-indigo-600 dark:from-indigo-400 dark:to-indigo-500'
  }, {
    title: 'Icon Packs',
    icon: <PackageIcon className="h-8 w-8 text-white" data-id="element-280" />,
    color: 'bg-gradient-to-br from-red-500 to-red-600 dark:from-red-400 dark:to-red-500'
  }, {
    title: '3D Assets & Mockups',
    icon: <div className="h-8 w-8 text-white" data-id="element-281" />,
    color: 'bg-gradient-to-br from-orange-500 to-orange-600 dark:from-orange-400 dark:to-orange-500'
  }, {
    title: 'Canva Templates',
    icon: <PaletteIcon className="h-8 w-8 text-white" data-id="element-282" />,
    color: 'bg-gradient-to-br from-teal-500 to-teal-600 dark:from-teal-400 dark:to-teal-500'
  }, {
    title: 'Social Media Content Packs',
    icon: <ActivityIcon className="h-8 w-8 text-white" data-id="element-283" />,
    color: 'bg-gradient-to-br from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0]'
  }, {
    title: 'Legal Templates',
    icon: <FileTextIcon className="h-8 w-8 text-white" data-id="element-284" />,
    color: 'bg-gradient-to-br from-blue-500 to-blue-600 dark:from-blue-400 dark:to-blue-500'
  }, {
    title: 'Resume & Portfolio Templates',
    icon: <BriefcaseIcon className="h-8 w-8 text-white" data-id="element-285" />,
    color: 'bg-gradient-to-br from-purple-500 to-purple-600 dark:from-purple-400 dark:to-purple-500'
  }];
  // Enhanced fix for scrolling issue - modified handleCategoryClick with longer delay
  const handleCategoryClick = (category: string) => {
    // Use a timeout with longer delay to ensure navigation happens after the current event loop
    // This prevents scroll blocking when dropdowns are open
    setTimeout(() => {
      // Use window.scrollTo to maintain the current scroll position
      const currentScrollPosition = window.scrollY;
      // Navigate to the browse page with the category parameter
      navigate(`/browse?category=${encodeURIComponent(category)}`);
      // Restore scroll position if needed
      if (currentScrollPosition > 0) {
        window.scrollTo(0, currentScrollPosition);
      }
    }, 100); // Increased from 50ms to 100ms for better timing
  };
  // Enhanced fix for scrolling issue - modified toggleCategoryView with longer delay
  const toggleCategoryView = (e: React.MouseEvent) => {
    // Stop propagation to prevent any parent handlers from interfering
    e.stopPropagation();
    // Explicitly prevent any default browser behavior that might affect scrolling
    if (e.nativeEvent) {
      e.nativeEvent.stopImmediatePropagation();
    }
    // Use a timeout with longer delay to ensure state change happens after the current event loop
    // This prevents scroll blocking when the dropdown is toggled
    setTimeout(() => {
      setShowAllCategories(!showAllCategories);
    }, 100); // Increased from 50ms to 100ms for better timing
  };
  // Get categories to display based on current state
  const displayCategories = showAllCategories ? categories : categories.slice(0, 10);
  return <section className="py-20 bg-gray-50 dark:bg-[#0A1C1A]" data-id="element-286">
      <div className="container mx-auto px-4 md:px-6" data-id="element-287">
        <div className="text-center mb-12" data-id="element-288">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-transparent bg-clip-text" data-id="element-289">
            Explore by Category
          </h2>
          <p className="text-xl text-gray-600 dark:text-[#B0C1BD] max-w-3xl mx-auto" data-id="element-290">
            Find the perfect digital assets for your next project
          </p>
        </div>
        <AnimatePresence data-id="element-291">
          <motion.div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6" layout data-id="element-292">
            {displayCategories.map((category, index) => <CategoryTile key={category.title} title={category.title} icon={category.icon} color={category.color} delay={index} onClick={() => handleCategoryClick(category.title)} data-id="element-293" />)}
          </motion.div>
        </AnimatePresence>
        {/* View All Categories button */}
        <div className="text-center mt-10" data-id="element-294">
          <button onClick={toggleCategoryView} className="bg-white dark:bg-[#162825] border border-gray-200 dark:border-[#1A3330] text-gray-800 dark:text-[#F8FAF9] px-8 py-3 rounded-full text-lg font-medium hover:border-[#2DB489] hover:text-[#2DB489] dark:hover:border-[#4ED1A0] dark:hover:text-[#4ED1A0] group relative overflow-hidden transition-all flex items-center justify-center mx-auto" data-id="element-295">
            <span className="relative z-10 flex items-center" data-id="element-296">
              {showAllCategories ? 'Show Less Categories' : 'View All Categories'}
              {showAllCategories ? <ChevronUpIcon className="ml-2 h-5 w-5" data-id="element-297" /> : <ChevronDownIcon className="ml-2 h-5 w-5" data-id="element-298" />}
            </span>
            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-[#0A3C52] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] group-hover:w-full transition-all duration-300" data-id="element-299"></span>
          </button>
        </div>
      </div>
    </section>;
};
// Export the CategoryGrid component
export const CategoryGrid: React.FC = () => <CategoryGridContent data-id="element-300" />;